import { initVersionedSchema } from '@principle-theorem/editor';
import {
  FailedDestinationEntityRecord,
  IDestinationEntityJobRunOptions,
  IHasSourceIdentifier,
  ITranslationMap,
  type IDestinationEntity,
  type IDestinationEntityRecord,
  type IGetRecordResponse,
  type IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  Brand,
  ClinicalNote,
  stafferToNamedDoc,
  toINamedDocuments,
} from '@principle-theorem/principle-core';
import {
  type IClinicalNote,
  type IPatient,
  type IStaffer,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  Firestore,
  INamedDocument,
  ISODateType,
  Timestamp,
  Timezone,
  WithPrimaryKey,
  getError,
  isSameRef,
  reduceToSingleArrayFn,
  resolveSequentially,
  snapshotCombineLatest,
  sortTimestamp,
  toISODate,
  toMomentTz,
  toNamedDocument,
  toTimestamp,
  type WithRef,
} from '@principle-theorem/shared';
import { groupBy } from 'lodash';
import { Observable, combineLatest, from, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { PATIENT_RESOURCE_TYPE } from '../../../destination/entities/patient';
import {
  BasePatientClinicalNoteDestinationEntity,
  IClinicalNoteJobData,
  IClinicalNoteMigrationData,
} from '../../../destination/entities/patient-clinical-notes';
import { PracticeMigration } from '../../../practice-migrations';
import { TranslationMapHandler } from '../../../translation-map';
import {
  CorePracticePatientTreatmentNote,
  PatientTreatmentNoteSourceEntity,
} from '../../source/entities/patient-treatment-notes';
import {
  CorePracticePatient,
  PatientSourceEntity,
} from '../../source/entities/patients';
import { PROVIDER_RESOURCE_TYPE } from '../../source/entities/providers';
import { CorePracticeItemCodeMappingHandler } from '../mappings/item-codes';
import { CorePracticeStafferMappingHandler } from '../mappings/staff';
import { PatientDestinationEntity } from './patients';

export class PatientClinicalNoteDestinationEntity extends BasePatientClinicalNoteDestinationEntity<CorePracticePatient> {
  patientSourceEntity = new PatientSourceEntity();

  override sourceEntities = {
    patients: new PatientSourceEntity(),
    treatments: new PatientTreatmentNoteSourceEntity(),
  };

  override destinationEntities = {
    patients: new PatientDestinationEntity(),
  };

  customMappings = {
    staff: new CorePracticeStafferMappingHandler(),
    itemCodes: new CorePracticeItemCodeMappingHandler(),
  };

  buildJobData$(
    migration: WithRef<IPracticeMigration>,
    destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<IClinicalNoteJobData<CorePracticePatient>[]> {
    const brand$ = PracticeMigration.brand$(migration).pipe(
      map(toNamedDocument)
    );
    const practitioners$ = from(brand$).pipe(
      switchMap((brand) => Firestore.getDocs(Brand.stafferCol(brand))),
      toINamedDocuments()
    );
    const staff$ = combineLatest([
      this.customMappings.staff.getRecords(translationMap),
      translationMap.getByType<IStaffer>(PROVIDER_RESOURCE_TYPE),
    ]).pipe(map(([staff, mappedStaff]) => [...staff, ...mappedStaff]));
    const sourceItemCodes$ =
      this.customMappings.itemCodes.getRecords(translationMap);

    return snapshotCombineLatest([
      staff$,
      practitioners$,
      sourceItemCodes$,
    ]).pipe(
      switchMap((dependencies) =>
        combineLatest([
          this.buildSourceRecordQuery$(
            migration,
            this.sourceEntities.patients,
            runOptions
          ),
          of(dependencies),
        ])
      ),
      map(([sourcePatients, [staff, practitioners, sourceItemCodes]]) =>
        sourcePatients.map((sourcePatient) => ({
          staff,
          practitioners,
          sourceItemCodes,
          sourcePatient,
          destinationEntity,
        }))
      )
    );
  }

  async buildMigrationData(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    data: IClinicalNoteJobData<CorePracticePatient>
  ): Promise<
    | IClinicalNoteMigrationData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord)
  > {
    try {
      const sourcePatientId = this.sourceEntities.patients
        .getSourceRecordId(data.sourcePatient.data)
        .toString();
      const patientRef = await translationMap.getDestination<IPatient>(
        sourcePatientId,
        PATIENT_RESOURCE_TYPE
      );

      if (!patientRef) {
        return this._buildErrorResponse(
          data.sourcePatient.record,
          `Couldn't resolve patient`
        );
      }

      const clinicalNotes = await this._buildClinicalNotes(
        migration,
        translationMap,
        data
      );

      return {
        sourcePatientId,
        patientRef,
        clinicalNotes,
      };
    } catch (error) {
      return this._buildErrorResponse(
        data.sourcePatient.record,
        getError(error)
      );
    }
  }

  private async _buildClinicalNotes(
    migration: WithRef<IPracticeMigration>,
    translationMap: TranslationMapHandler,
    data: IClinicalNoteJobData<CorePracticePatient>
  ): Promise<(IClinicalNote & IHasSourceIdentifier)[]> {
    const timezone = migration.configuration.timezone;
    const patientUid = this.sourceEntities.patients.getSourceRecordId(
      data.sourcePatient.data
    );
    const clinicalNoteTreatments =
      await this.sourceEntities.treatments.filterRecords(
        migration,
        'patientId',
        patientUid,
        (record) => !!record.data.note
      );

    const sorted = clinicalNoteTreatments.sort((treatmentA, treatmentB) =>
      sortTimestamp(
        this._getNoteTimestamp(treatmentA, timezone),
        this._getNoteTimestamp(treatmentB, timezone)
      )
    );
    const userGroupedNotes = groupBy(
      sorted,
      (record) => record.data.providerId
    );

    const clinicalNotes = await resolveSequentially(
      Object.entries(userGroupedNotes),
      async ([providerId, userNotes]) => {
        const staffer = await getClinicalNotePractitioner(
          providerId,
          data.staff,
          data.practitioners,
          translationMap
        );
        if (!staffer) {
          throw new Error(`Failed to resolve practitioner code: ${providerId}`);
        }
        const dateGroupedNotes = groupBy(userNotes, (note) =>
          this._getNoteDate(note)
        );

        return Object.values(dateGroupedNotes).map((notes) => {
          const content = notes
            .reduce(
              (
                results: IGetRecordResponse<CorePracticePatientTreatmentNote>[],
                note
              ) => {
                const existing = results.find(
                  (result) => result.data.note === note.data.note
                );
                if (!existing) {
                  results.push(note);
                }
                return results;
              },
              []
            )
            .map((note) => note.data.note ?? '');

          const noteDate = this._getNoteDate(notes[0]);
          const noteTimestamp = this._getNoteTimestamp(notes[0], timezone);

          return {
            sourceIdentifier: `${notes[0].data.patientId}-${noteDate}-${providerId}`,
            ...ClinicalNote.init({
              owner: stafferToNamedDoc(staffer),
              content: initVersionedSchema(content),
              recordDate: noteDate ?? toISODate(toTimestamp()),
              createdAt: noteTimestamp,
              updatedAt: noteTimestamp,
              immutable: true,
            }),
          };
        });
      }
    );
    return clinicalNotes.reduce(reduceToSingleArrayFn, []);
  }

  private _getNoteTimestamp(
    sourceNote: IGetRecordResponse<CorePracticePatientTreatmentNote>,
    timezone: Timezone
  ): Timestamp {
    const noteDate = this._getNoteDate(sourceNote);
    return noteDate
      ? toTimestamp(toMomentTz(noteDate, timezone))
      : toTimestamp();
  }

  private _getNoteDate(
    sourceNote: IGetRecordResponse<CorePracticePatientTreatmentNote>
  ): ISODateType | undefined {
    return (
      sourceNote.record.translations.completeDate ??
      sourceNote.record.translations.planDate
    );
  }
}

async function getClinicalNotePractitioner(
  practitionerId: string,
  staff: WithPrimaryKey<ITranslationMap<IStaffer>>[],
  practitioners: INamedDocument<IStaffer>[],
  translationMap: TranslationMapHandler
): Promise<INamedDocument<IStaffer>> {
  const practitionerMap =
    staff.find((staffer) => staffer.sourceIdentifier === practitionerId)
      ?.destinationIdentifier ||
    ((await translationMap.getBySource(practitionerId, PROVIDER_RESOURCE_TYPE))
      ?.destinationIdentifier as DocumentReference<IStaffer> | undefined);

  if (!practitionerMap) {
    throw new Error(`Couldn't find practitioner with id ${practitionerId}`);
  }

  const existingPractitioner = practitioners.find((practitioner) =>
    isSameRef(practitioner, practitionerMap)
  );

  if (existingPractitioner) {
    return existingPractitioner;
  }

  const creator = await Firestore.getDoc(practitionerMap);
  return stafferToNamedDoc(creator);
}
